import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Loader2, ScrollText, Search, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_BADGE = {
  admin: 'bg-indigo-600 text-white hover:bg-indigo-600',
  client: 'bg-emerald-600 text-white hover:bg-emerald-600',
  lab: 'bg-amber-500 text-white hover:bg-amber-500',
  system: 'bg-slate-500 text-white hover:bg-slate-500',
};

const AuditLogs = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [actorRole, setActorRole] = useState('');
  const [action, setAction] = useState('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (actorRole) params.actor_role = actorRole;
      if (action) params.action = action;
      if (q) params.q = q;
      if (from) params.from = from;
      if (to) params.to = to;
      const res = await axios.get(`${API}/admin/audit-logs`, { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Erreur de chargement du journal');
    } finally {
      setLoading(false);
    }
  }, [page, limit, actorRole, action, q, from, to]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <AdminLayout title="Journal d'audit" subtitle="Toutes les actions tracées (rétention illimitée)">
      <Card data-testid="audit-page">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <ScrollText className="h-5 w-5" />
            Événements ({total})
          </CardTitle>
          <Button variant="outline" onClick={() => { setPage(1); fetchLogs(); }} data-testid="audit-refresh">
            <RefreshCw className="h-4 w-4 mr-2" />Actualiser
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher (acteur, cible, action)"
                className="pl-10"
                value={q}
                onChange={(e) => { setPage(1); setQ(e.target.value); }}
                data-testid="audit-search"
              />
            </div>
            <Select value={actorRole || 'all'} onValueChange={(v) => { setPage(1); setActorRole(v === 'all' ? '' : v); }}>
              <SelectTrigger data-testid="audit-role-filter"><SelectValue placeholder="Tous les rôles" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les rôles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="client">Client (dentiste)</SelectItem>
                <SelectItem value="lab">Laboratoire</SelectItem>
                <SelectItem value="system">Système</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={from} onChange={(e) => { setPage(1); setFrom(e.target.value); }} data-testid="audit-from" />
            <Input type="date" value={to} onChange={(e) => { setPage(1); setTo(e.target.value); }} data-testid="audit-to" />
          </div>

          {loading ? (
            <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">Aucun événement</div>
          ) : (
            <div className="rounded-lg border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Date / heure</TableHead>
                    <TableHead>Rôle</TableHead>
                    <TableHead>Acteur</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Cible</TableHead>
                    <TableHead>Détails</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.id} data-testid={`audit-row-${it.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{new Date(it.timestamp).toLocaleString('fr-FR')}</TableCell>
                      <TableCell><Badge className={ROLE_BADGE[it.actor_role] || ROLE_BADGE.system}>{it.actor_role}</Badge></TableCell>
                      <TableCell className="text-sm">{it.actor_name || '—'}</TableCell>
                      <TableCell className="text-sm font-mono">{it.action}</TableCell>
                      <TableCell className="text-xs">{it.target_type ? `${it.target_type}: ${it.target_id?.slice(0, 8) || '—'}` : '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-md truncate" title={JSON.stringify(it.details || {})}>{JSON.stringify(it.details || {})}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="flex items-center justify-between mt-4 text-sm">
            <span className="text-muted-foreground">Page {page} / {totalPages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="audit-prev">Précédent</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="audit-next">Suivant</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default AuditLogs;
